import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import usePromotions from "common/datahooks/usePromotions";
import { get } from "common/helpers/HTTP";
import type { SubscriptionType } from "common/types";

import type { Invoice } from "../types";

const INVOICE_TYPE_QUERY_PARAM: Record<
  Exclude<SubscriptionType, "agency">,
  string
> = {
  user: "aimfox",
  rental: "rental",
};

async function getInvoices(
  workspaceId: string,
  type: SubscriptionType,
): Promise<Invoice[]> {
  if (type === "agency") {
    return (await get(`subscriptions/invoices`)).invoices;
  }

  const searchParams = new URLSearchParams();
  searchParams.append("subscription_type", INVOICE_TYPE_QUERY_PARAM[type]);

  return (
    await get(`workspaces/${workspaceId}/invoices?${searchParams.toString()}`)
  ).invoices;
}

export default function useInvoices(type: SubscriptionType) {
  const {
    id: workspaceId,
    subscription,
    rental_subscriptions,
  } = useSelectedWorkspaceContext();
  const { agencyPlan } = usePromotions();
  const { invoicesKeys } = getQueryKeys(workspaceId);

  const enabledMap: Record<SubscriptionType, boolean> = {
    agency: !!agencyPlan,
    rental: rental_subscriptions?.length > 0,
    user: !!subscription,
  };

  const {
    data: invoices,
    isLoading: isLoadingInvoices,
    isError: isInvoicesError,
    refetch: refetchInvoices,
  } = useQuery({
    queryKey: invoicesKeys.list(type),
    queryFn: () => getInvoices(workspaceId, type),
    enabled: enabledMap[type],
  });

  return {
    invoices,
    isLoadingInvoices,
    isInvoicesError,
    refetchInvoices,
  };
}
